/**
 * Hard ceiling on consult recording length (CLA-969).
 *
 * A recording left running — the clinician walked away, the laptop lid stayed
 * open over lunch — used to run until the tab died. By then it was hundreds of
 * megabytes held in memory, and the stop path read, decoded and hashed all of
 * it in one go, which is how a forgotten recording turned into a crashed tab
 * and a lost consult. Six hours is far past any real consult, so reaching it
 * means "left running", and the right response is to finish the recording
 * exactly as if Stop had been pressed: keep the audio, commit it, tell the
 * clinician why it ended. Never discard.
 *
 * Pure policy only — no store, no timers — so the thresholds are testable on
 * their own and the store has one place to ask.
 */

/**
 * Six hours of RECORDED time — the store's `elapsed` semantics (CLA-1267):
 * paused time and capture-lost time do not count, so a consult paused over a
 * long break is not cut short by it.
 */
export const RECORDING_CEILING_SECONDS = 6 * 60 * 60

/**
 * Why a recording stopped when it was NOT the clinician's own Stop. Mirrors the
 * server's `Note.stopped_reason` values (claro-elixir; an open set on the wire);
 * sent as the `stopped_reason` form field on `POST /notes/generate`. A normal
 * Stop sends nothing.
 *
 * `duration_ceiling` — 6 hours of recorded time (CLA-969).
 * `silence_timeout` — 60 minutes of continuous silence (CLA-1564).
 */
export type StoppedReason = 'duration_ceiling' | 'silence_timeout'

export const STOPPED_REASON_DURATION_CEILING: StoppedReason = 'duration_ceiling'
export const STOPPED_REASON_SILENCE_TIMEOUT: StoppedReason = 'silence_timeout'

const STOPPED_REASONS: readonly StoppedReason[] = [
  STOPPED_REASON_DURATION_CEILING,
  STOPPED_REASON_SILENCE_TIMEOUT,
]

/**
 * Narrow an untrusted value to a `StoppedReason`, or null.
 *
 * Used wherever a reason crosses a boundary it could be corrupted at: an
 * IndexedDB row written by an older build (no field at all), or a caller that
 * wires `stopRecording` straight to an `onClick` and hands it a MouseEvent. A
 * normal Stop must never be reported to the server as an automatic one.
 */
export function toStoppedReason(value: unknown): StoppedReason | null {
  return STOPPED_REASONS.find((r) => r === value) ?? null
}

/** Has this recording reached its ceiling? `recordedSeconds` is `elapsed`. */
export function hasReachedRecordingCeiling(recordedSeconds: number): boolean {
  return recordedSeconds >= RECORDING_CEILING_SECONDS
}

/**
 * The stop-time silence check only ever runs on SHORT recordings.
 *
 * The check exists to catch a false start on a muted or dead mic — "No sound
 * was captured, record again" — and when it fires the audio is DISCARDED, not
 * saved. It also works by decoding the whole recording to PCM, which costs
 * ~11.5 MB per minute of mono audio at 48 kHz (twice that for stereo): an hour
 * is ~700 MB, six hours ~4 GB, which is a crashed tab rather than a result.
 *
 * Ten minutes bounds that decode at ~115 MB (mono), which every recording up
 * to ten minutes already paid before this limit existed. Past it, the case the
 * check is for no longer applies: the live level monitor has been warning
 * "no audio" on screen since five seconds in, so a clinician who recorded ten
 * minutes of silence was told at the time — and uploading a silent recording
 * costs a failed note, whereas discarding a real one costs the consult.
 */
export const SILENCE_CHECK_MAX_SECONDS = 10 * 60

/**
 * Backstop on the blob itself, for a recorder whose byte count and recorded
 * time disagree. `elapsed` excludes capture-lost stretches (CLA-1341), but a
 * recorder on a muted track keeps emitting silent chunks the whole time — so a
 * phone locked for hours can hold hours of audio behind a ten-minute clock.
 * Ten minutes at the requested 64 kbps is ~4.8 MB; this allows twice that. A
 * browser that ignores the requested bitrate simply skips the check sooner,
 * which is the safe direction.
 */
export const SILENCE_CHECK_MAX_BYTES = 10 * 1024 * 1024

/**
 * Should stop run the (destructive, memory-hungry) silence check?
 *
 * Only for a clinician's own Stop, on a recording that is short by BOTH its
 * clock and its size. Every "no" here means the recording is saved and
 * committed like any other.
 */
export function shouldCheckForSilence(opts: {
  recordedSeconds: number
  bytes: number
  stoppedReason: StoppedReason | null
}): boolean {
  if (opts.stoppedReason !== null) return false
  // Under two seconds is a tap-and-stop; there is too little to judge.
  if (opts.recordedSeconds < 2) return false
  if (opts.recordedSeconds >= SILENCE_CHECK_MAX_SECONDS) return false
  if (opts.bytes > SILENCE_CHECK_MAX_BYTES) return false
  return true
}
